import React, { useEffect, useState } from 'react';
import { DATABASE_TYPES, MONGODB_EDITIONS, ROUTE } from 'constant';
import { actions } from 'redux/actions';
import Shared from 'screens/shared';
import { useSelector } from 'react-redux';
import { ACTIONS_TYPES } from 'redux/helper';
import {
  isEmptyArray,
  _t,
  pushHistory,
  copyToClipboard, formatFileSizeBinary, uppercaseUnderscore,
} from 'utils';
import { getLocaleDateTime } from 'utils/time';
import { checkFreeOfCharge, generateTemplateListItem, getMinDateTimeForBackup } from 'utils/database';
import { IconButton } from '@mui/material';
import { useNotification } from 'screens/shared/hooks/notifications';
import { form, formField } from 'types';
import UpdateForm from './update-form';
import UserForm from './user-form';

export default ({ match, history }) => {
  const [,, setNotification] = useNotification({});
  const [hideAddDialog, setHideAddDialog] = useState(null);
  const [isRestore, setRestoreConfirmation] = useState(false);
  const [isFreeOfCharge, setIsFreeOfCharge] = useState(false);

  const [backupValues, setBackupValues] = useState({});

  const clusterId = match?.params?.id;

  const {
    [ACTIONS_TYPES.DATABASE.MONGODB.GET]: databaseCluster = {},
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL]: databaseClusters = [],
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL_USERS]: users = {},
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_TEMPLATES]: templates = [],
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_SNAPSHOTS_FOR_CLUSTER]: snapshots = [],
    [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [],
  } = useSelector((e) => e);

  useEffect(() => {
    if (clusterId) {
      actions.database.mongodb.get({ id: clusterId });
      actions.database.mongodb.getAllMongoDBUsers({ clusterId });
      actions.database.mongodb.getSnapshotsForCluster({ clusterId });
      if (isEmptyArray(dataCenters)) actions.dataCenter.getAll({});
      if (templates.length === 0) actions.database.mongodb.getTemplates();
    }
    if (databaseClusters.length === 0) {
      actions.database.mongodb.getAll();
    }
    // eslint-disable-next-line
  }, [clusterId]);

  const handleFreeOfChargeCheck = () => {
    if (databaseCluster.id) {
      const isFree = checkFreeOfCharge({
        databaseClusters,
        templates,
        values: databaseCluster,
      });
      setIsFreeOfCharge(isFree);
    }
  };

  useEffect(() => {
    if (databaseClusters.length > 0) {
      handleFreeOfChargeCheck();
    }
    // eslint-disable-next-line
  }, [databaseCluster]);

  useEffect(() => {
    if (databaseClusters.length > 0) {
      handleFreeOfChargeCheck();
    }
    // eslint-disable-next-line
  }, [databaseClusters]);

  const deleteHandler = () => {
    actions.database.mongodb.delete({ id: clusterId });
    pushHistory({ history, path: ROUTE.DASHBOARD.DATABASES.path });
  };

  const getDataCenterName = (dcId) => {
    const { properties = {} } = isEmptyArray(dataCenters) ? {} : dataCenters.find(({ id }) => id === dcId) || {};
    return properties.name;
  };

  const onDeleteUser = (e) => {
    const { username } = e.properties;
    actions.database.mongodb.deleteMongoDbUser({ clusterId, username }).then(() => {
      actions.database.mongodb.getAllMongoDBUsers({ clusterId });
    });
  };

  const onAddedUser = (e) => {
    if (!e || e.httpStatus) return;
    actions.database.mongodb.getAllMongoDBUsers({ clusterId }).then(() => {
      setHideAddDialog(false);
      setTimeout(() => setHideAddDialog(null), 100);
    });
  };

  // const monitorClickHandler = () => {
  //   pushHistory({ history, path: ROUTE.DASHBOARD.DATABASE_MONITORING.path.replace(':id', clusterId) });
  // };

  const templateId = databaseCluster?.properties?.templateID;
  const template = templates.find((item) => item.id === templateId);
  const edition = databaseCluster?.properties?.edition;
  const isEnterprise = edition === MONGODB_EDITIONS.ENTERPRISE;

  const copyText = (str) => {
    copyToClipboard(str, 'Connection string copied to clipboard');
    setNotification({
      type: 'success',
      message: _t('DATABASES.MONGODB.ACTION_TEXTS.CONNECTION_COPIED'),
    });
  };

  const copyBiConnectorUri = (str) => {
    copyToClipboard(str);
    setNotification({
      type: 'success',
      message: _t('DATABASES.PROPERTIES.MONGODB_BI_CONNECTOR.URI_COPIED'),
    });
  };

  const onBackupRequested = (item) => {
    setBackupValues({ snapshotId: item?.id });
    setRestoreConfirmation(true);
  };

  const onPITRRequested = ({ recoveryTargetTime }) => {
    setBackupValues({ recoveryTargetTime });
    setRestoreConfirmation(true);
  };

  const confirmRestore = (valid) => {
    setRestoreConfirmation(false);
    if (valid) {
      const { snapshotId, recoveryTargetTime } = backupValues;
      actions.database.mongodb.restoreCluster({ clusterId, snapshotId, recoveryTargetTime }).then(() => {
        setBackupValues({});
        setTimeout(() => {
          actions.database.mongodb.getSnapshotsForCluster({ clusterId });
          actions.database.mongodb.get({ id: clusterId });
        }, 500);
      });
    }
  };
  return (
    <Shared.widgets.Page
      route={ROUTE.DASHBOARD.DATABASES}
      title={_t('DATABASES.DATABASE')}
      strapIcon="database"
      strapTitle={databaseCluster?.properties?.displayName}
      strapSubTitle={(
        <div className="m-t-5">
          <span className="m-r-5">{_t('DATABASES.MONGODB.STATE')}</span>
          <Shared.utilities.Status className="m-r-5" state={databaseCluster?.metadata?.state} />
          <span className="m-r-5">{_t('DATABASES.MONGODB.HEALTH_STATE')}</span>
          <Shared.utilities.Status state={databaseCluster?.metadata?.health} />
        </div>
      )}
      onDelete={deleteHandler}
      editForm={<UpdateForm values={databaseCluster} />}
      editAction={ACTIONS_TYPES.DATABASE.MONGODB.UPDATE}
      isEditButton
    >
      <Shared.widgets.Section
        isAddButton
        isEditButton
        editForm={(
          <UserForm
            cluster={databaseCluster}
            fieldOptions={['password', 'roles']}
          />
        )}
        inlineEdit
        showAddForm={hideAddDialog}
        createForm={<UserForm cluster={databaseCluster} onSubmit={onAddedUser} />}
        sectionTitle={_t('DATABASES.USER_MANAGEMENT.TITLE')}
        itemIcon="user"
        items={users}
        itemOnDelete={onDeleteUser}
        itemOnEdit={onAddedUser}
        setItemTitle={(e) => e.properties.username}
        setItemDetails={(e) => (
          <>
            <div className="wordBreak">{`Role(s): ${e.properties.roles.map((item) => `${item.database || '*'} (${item.role})`).join(', ')}`}</div>
          </>
        )}
        style={{ margin: '20px 0' }}
      />
      <Shared.widgets.Properties
        title={_t('DATABASES.PROPERTIES.TITLE')}
        properties={[
          ['UUID', { value: clusterId }],
          [_t('DATABASES.PROPERTIES.EDITION'), _t(`DATABASES.EDITIONS.${databaseCluster?.properties?.edition?.toUpperCase()}`)],
          // [_t('DATABASES.PROPERTIES.MONITOR_DATABASES'), (
          //   // <span className="color-link pointer" onClick={monitorClickHandler}>
          //   <span className="color-link pointer">
          //     <Shared.widgets.Icon name="chart-line" />
          //     <span className="m-l-5">{database?.properties?.displayName}</span>
          //   </span>
          // )],
          ...(edition !== MONGODB_EDITIONS.ENTERPRISE
            ? [[_t('DATABASES.PROPERTIES.TEMPLATE'), template && generateTemplateListItem(template)]]
            : [
              [_t('DATABASES.PROPERTIES.CORE_COUNT'), { value: databaseCluster?.properties?.cores }],
              [_t('DATABASES.PROPERTIES.RAM_SIZE'), { value: databaseCluster?.properties?.ram, suffix: ' GB' }],
              [
                _t('DATABASES.PROPERTIES.STORAGE'),
                { value: databaseCluster?.properties?.storageSize, suffix: ` GB (${databaseCluster?.properties?.storageType})` },
              ],
            ]),
          [_t('DATABASES.PROPERTIES.LOCATION'), <Shared.utilities.Location location={databaseCluster?.properties?.location} />],
          ['MongoDB Version', { prefix: 'v', value: databaseCluster?.properties?.mongoDBVersion }],
          [_t('DATABASES.DB_TYPES'), databaseCluster?.properties?.type && _t(`DATABASES.DB_TYPES.${uppercaseUnderscore(databaseCluster.properties.type)}`)],
          ...(databaseCluster?.properties?.type === 'sharded-cluster'
            ? [[_t('DATABASES.PROPERTIES.SHARDS'), databaseCluster?.properties?.shards]]
            : []),
          [_t('DATABASES.PROPERTIES.INSTANCES'), databaseCluster?.properties?.instances],
          ...(isEnterprise
            ? [
              [_t('DATABASES.FORM_FIELDS.BACKUP_LOCATION.TITLE'), { value: databaseCluster?.properties?.backup?.location }],
            ] : []),
          ...(isEnterprise
            ? [
              ['BI Connector Status',
                <>
                  {
                    databaseCluster?.properties?.biConnector?.enabled
                      ? (
                        <>
                          <div className="flex align-items-center justify-content-start" style={{ gap: 30, lineHeight: 2 }}>
                            <span>
                              {`${databaseCluster?.properties?.biConnector?.host }:${ databaseCluster?.properties?.biConnector?.port}`}
                            </span>
                            <span style={{ flex: 1, textAlign: 'end' }}>
                              <IconButton
                                onClick={() => copyBiConnectorUri(`${databaseCluster?.properties?.biConnector?.host
                                }:${databaseCluster?.properties?.biConnector?.port}`)}
                                size="small"
                              >
                                <Shared.widgets.Icon
                                  name="copy"
                                  style={{
                                    float: 'right',
                                    color: 'grey',
                                    cursor: 'pointer',
                                  }}
                                />
                              </IconButton>
                            </span>
                          </div>
                        </>
                      )
                      : (
                        <span>
                          {_t('DATABASES.PROPERTIES.MONGODB_BI_CONNECTOR.DISABLED_TIP')}
                        </span>
                      )
                  }
                </>,
              ],
            ] : []),
          [_t('DATABASES.PROPERTIES.CONNECTION_LINK'), (
            <div className="flex flex-align-center flex-justify-between">
              <span>{databaseCluster?.properties?.connectionString}</span>
              <IconButton
                onClick={() => copyText(databaseCluster?.properties?.connectionString)}
                size="small"
              >
                <Shared.widgets.Icon
                  name="copy"
                  style={{
                    float: 'right',
                    color: 'grey',
                    cursor: 'pointer',
                  }}
                />
              </IconButton>
            </div>
          )],
          [<span style={{ fontSize: 16, color: 'var(--color-base)' }}>{_t('DATABASES.PROPERTIES.MAINTENANCE_WINDOW')}</span>],
          [_t('DATABASES.PROPERTIES.TIME'), databaseCluster?.properties?.maintenanceWindow?.time],
          [_t('DATABASES.PROPERTIES.WEEKDAY'), _t(`days.${databaseCluster?.properties?.maintenanceWindow?.dayOfTheWeek.toLowerCase()}`)],
          [<Shared.billing.DBaaSPriceEstimation
            freeOfCharge={isFreeOfCharge}
            databaseType={DATABASE_TYPES.MONGODB}
            model={{
              ...databaseCluster?.properties,
            }}
          />],
        ]}
      />

      <Shared.widgets.Section
        sectionTitle={_t('DATABASES.CLUSTER_TO_DC_CONNECTION.TITLE')}
        itemIcon="boxes"
        items={databaseCluster?.properties?.connections || []}
        setItemTitle={(e) => getDataCenterName(e.datacenterId)}
        setItemDetails={(e) => (
          <>
            <span>{`LAN: ${e.lanId}`}</span>
            <Shared.utilities.Separator />
            <span>{`IP(s): ${e.cidrList.join(', ')}`}</span>
          </>
        )}
        style={{ margin: '20px 0' }}
        tableColumns={['', 'VDC', 'LAN / IP\'s']}
      />

      <Shared.widgets.Section
        sectionTitle={_t('DATABASES.SNAPSHOTS.TITLE')}
        itemIcon="boxes"
        items={snapshots || []}
        setItemTitle={(e) => `${_t('DATABASES.BACKUPS.CREATED')}: ${getLocaleDateTime({ date: e.properties.creationTime })}`}
        setItemDetails={(e) => (
          <>
            <div>{`Version: ${e.properties.version}`}</div>
            <div>{`${_t('DATABASES.BACKUPS.SIZE')}: ${e.properties.size ? formatFileSizeBinary(e.properties.size * 1024 * 1024) : 'n/a'}`}</div>
          </>
        )}
        style={{ margin: '20px 0' }}
        actionButton={{
          onClick: (e, val) => onBackupRequested(val),
          title: _t('DATABASES.RESTORE.TITLE'),
          condition: (item) => item?.metadata?.state === 'AVAILABLE',
        }}
        customTopbarForm={edition === MONGODB_EDITIONS.ENTERPRISE && snapshots.length > 0 && {
          title: _t('DATABASES.RESTORE.BY_PITR'),
          formProps: () => {
            const latestSnapshot = snapshots[snapshots.length - 1];
            const recoveryTargetTime = new Date();
            const minDateTime = getMinDateTimeForBackup({ backup: latestSnapshot, byCreationTime: true, retentionDays: 0 });

            return form({
              fieldsValues: {
                recoveryTargetTime,
              },
              fields: [
                formField.mobileDateTime({
                  title: _t('DATABASES.BACKUPS.BACKUP_TIME'), name: 'recoveryTargetTime', maxDateTime: recoveryTargetTime, minDateTime,
                }),
              ],
              onSubmit: onPITRRequested,
            });
          },
        }}
      />
      <Shared.dialog.Confirm show={isRestore} onClose={confirmRestore} title={_t('SHARED.CONFIRM_PASSWORD.TITLE')} typeText="RESTORE" isPassword />
    </Shared.widgets.Page>
  );
};
